/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useEffect, useState } from 'react';
import { range } from 'lodash';
import { Box } from 'grommet';
import { LoadingSpinner } from '@blend/enterprise-components';
import styled from 'styled-components';

interface ReactPdfLib {
  Document: typeof import('react-pdf').Document;
  Page: typeof import('react-pdf').Page;
}

export namespace PDFViewer {
  export interface Props {
    file: string | Blob;
    pageWidth?: number;
  }
}

const PageWrapper = styled(Box).attrs({
  elevation: 'light',
  margin: 'small',
})``;

export const PDFViewer: React.FC<PDFViewer.Props> = ({ file, pageWidth = 1024 }) => {
  const [reactPdfLib, setReactPdfLib] = useState<ReactPdfLib>();
  const [numPages, setNumPages] = useState<number | undefined>();

  const onDocumentLoadSuccess = ({ numPages: pageCount }: { numPages: number }) => {
    setNumPages(pageCount);
  };

  const renderLoadingSpinner = () => (
    <Box align="center" flex="grow" height="medium" justify="center">
      <LoadingSpinner size="lg" theme="secondary" />
    </Box>
  );

  useEffect(() => {
    const importReactPDF = async (): Promise<void> => {
      // lazily load react-pdf library
      const { Document, Page, pdfjs } = await import('react-pdf');
      // we need to set the workerSrc, more details here https://github.com/wojtekmaj/react-pdf#standard-browserify-and-others
      pdfjs.GlobalWorkerOptions.workerSrc = '/assets/js/pdf.worker.min.v2.9.359.js';
      setReactPdfLib({ Document, Page });
    };
    importReactPDF();
  }, []);

  if (!reactPdfLib) {
    return renderLoadingSpinner();
  }
  const { Document, Page } = reactPdfLib;
  return (
    <Document file={file} loading={renderLoadingSpinner} onLoadSuccess={onDocumentLoadSuccess}>
      <Box align="center" background="backgroundGrey" id="it-pdfDocuments">
        {numPages &&
          range(0, numPages).map(index => (
            <PageWrapper id={`it-pageWrapper-${index}`} key={index}>
              <Page key={`page_${index + 1}`} pageNumber={index + 1} scale={1} width={pageWidth} />
            </PageWrapper>
          ))}
      </Box>
    </Document>
  );
};
